import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { CURRENCIES, fmt, fmtIDR, fmtNum } from "../lib/format";
import { Boxes, Plus, Loader2, History, Flame } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Modal } from "./OfficerHome";

export default function Inventory() {
  const { user } = useAuth();
  const [inv, setInv] = useState([]);
  const [adjust, setAdjust] = useState(false);
  const [liq, setLiq] = useState(false);
  const [moves, setMoves] = useState([]);
  const load = () => {
    api.get("/inventory").then((r) => setInv(r.data)).catch(() => {});
    if (user?.role === "owner") api.get("/inventory/movements").then((r) => setMoves(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const total = inv.reduce((s, i) => s + Number(i.idr_value), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Boxes className="h-6 w-6 text-[#EAB308]" />
          <h1 className="font-head text-2xl font-bold">Inventory</h1>
        </div>
        {user?.role === "owner" && (
          <div className="flex items-center gap-2">
            <button className="btn-outline flex items-center gap-2" data-testid="liquidation-btn" onClick={() => setLiq(true)}>
              <Flame className="h-4 w-4" /> Buang Stock
            </button>
            <button className="btn-gold flex items-center gap-2" data-testid="adjust-inventory-btn" onClick={() => setAdjust(true)}>
              <Plus className="h-4 w-4" /> Set Stok
            </button>
          </div>
        )}
      </div>

      <div className="nnx-card p-5" data-testid="inventory-total">
        <div className="label">Total Nilai Inventory (IDR)</div>
        <div className="mono font-bold text-3xl text-[#EAB308] mt-1">{fmtIDR(total)}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {inv.map((i) => {
          const neg = Number(i.balance) < 0;
          return (
            <div key={i.currency} className={`nnx-card p-4 ${neg ? "border-rose-500/40" : ""}`} data-testid={`inv-${i.currency}`}>
              <div className="flex items-center justify-between">
                <div className="font-head font-bold text-lg">{i.symbol} {i.currency}</div>
                <div className={`text-xs mono ${Number(i.realized_profit) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>PnL {fmtIDR(i.realized_profit)}</div>
              </div>
              <div className={`mono font-bold text-2xl mt-2 ${neg ? "text-rose-400" : ""}`}>{fmtNum(i.balance, i.currency)}</div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div><div className="text-[#64748B]">Avg Cost</div><div className="mono">{i.currency === "IDR" ? "-" : fmtIDR(i.avg_cost_idr)}</div></div>
                <div><div className="text-[#64748B]">Nilai IDR</div><div className="mono text-[#EAB308]">{fmtIDR(i.idr_value)}</div></div>
                <div><div className="text-[#64748B]">Inflow</div><div className="mono">{fmtNum(i.total_inflow, i.currency)}</div></div>
                <div><div className="text-[#64748B]">Outflow</div><div className="mono">{fmtNum(i.total_outflow, i.currency)}</div></div>
              </div>
            </div>
          );
        })}
      </div>

      {user?.role === "owner" && (
        <div className="nnx-card p-5" data-testid="inventory-correction-log">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-5 w-5 text-[#EAB308]" />
            <h2 className="font-head font-bold text-lg">Riwayat Koreksi Stok</h2>
          </div>
          {moves.length === 0 ? (
            <div className="text-sm text-[#64748B]">Belum ada koreksi stok.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[#64748B] border-b border-[#222B3D]">
                    <th className="py-2 pr-3">Waktu</th>
                    <th className="py-2 pr-3">Currency</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3 text-right">Perubahan</th>
                    <th className="py-2 pr-3 text-right">Saldo Akhir</th>
                    <th className="py-2 pr-3 text-right">Cost/unit</th>
                    <th className="py-2">Catatan</th>
                  </tr>
                </thead>
                <tbody>
                  {moves.map((m, idx) => {
                    const amt = Number(m.amount);
                    return (
                      <tr key={idx} className="border-b border-[#151B28]" data-testid={`correction-row-${idx}`}>
                        <td className="py-2 pr-3 mono text-xs text-[#94A3B8]">{new Date(m.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="py-2 pr-3 font-semibold">{m.currency}</td>
                        <td className="py-2 pr-3"><span className={`text-xs px-1.5 py-0.5 rounded ${m.mode === "set" ? "bg-amber-500/15 text-amber-400" : "bg-sky-500/15 text-sky-400"}`}>{m.mode === "set" ? "Set Absolut" : "Tambah"}</span></td>
                        <td className={`py-2 pr-3 text-right mono ${amt >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{amt >= 0 ? "+" : ""}{fmtNum(m.amount, m.currency)}</td>
                        <td className="py-2 pr-3 text-right mono">{fmtNum(m.balance_after, m.currency)}</td>
                        <td className="py-2 pr-3 text-right mono text-[#94A3B8]">{m.currency === "IDR" ? "-" : fmtIDR(m.cost_per_unit_idr)}</td>
                        <td className="py-2 text-[#94A3B8]">{m.note || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {adjust && <AdjustModal onClose={() => setAdjust(false)} onDone={() => { setAdjust(false); load(); }} />}
      {liq && <LiquidationModal onClose={() => setLiq(false)} onDone={() => { setLiq(false); load(); }} />}
    </div>
  );
}

function AdjustModal({ onClose, onDone }) {
  const [f, setF] = useState({ currency: "IDR", amount: "", cost_per_unit_idr: "", note: "Koreksi stok", mode: "add" });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (f.amount === "" || f.amount === null) { toast.error("Jumlah wajib diisi"); return; }
    if (isNaN(Number(f.amount))) { toast.error("Jumlah harus berupa angka"); return; }
    if (f.mode === "set" && !f.note.trim()) { toast.error("Catatan wajib diisi untuk koreksi Set Absolut"); return; }
    if (f.mode === "set" && !window.confirm(`Timpa saldo ${f.currency} menjadi ${f.amount}? Nilai lama akan hilang.`)) return;
    setLoading(true);
    try {
      await api.post("/inventory/adjust", f);
      toast.success(`Stok ${f.currency} diperbarui`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title="Revisi / Set Stok Inventory" onClose={onClose} testid="adjust-modal">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button data-testid="adjust-mode-add" onClick={() => setF({ ...f, mode: "add" })}
          className={`chip py-2.5 font-semibold ${f.mode === "add" ? "bg-[#EAB308] text-[#0B0E14] border-[#EAB308]" : ""}`}>Tambah (+/−)</button>
        <button data-testid="adjust-mode-set" onClick={() => setF({ ...f, mode: "set" })}
          className={`chip py-2.5 font-semibold ${f.mode === "set" ? "bg-[#EAB308] text-[#0B0E14] border-[#EAB308]" : ""}`}>Set Absolut (koreksi)</button>
      </div>
      <p className="text-sm text-[#94A3B8] mb-3">{f.mode === "set"
        ? "Timpa saldo ke nilai benar (untuk koreksi salah input). Avg cost = cost yang Anda isi."
        : "Tambah/kurangi stok dengan cost basis (WAC di-blend). Isi jumlah negatif untuk mengurangi."}</p>
      <div className="space-y-3">
        <div><label className="label">Currency</label>
          <select data-testid="adjust-currency" className="nnx-input mt-1" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
        </div>
        <div><label className="label">Jumlah</label><input data-testid="adjust-amount" inputMode="decimal" className="nnx-input mono mt-1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0" /></div>
        {f.currency !== "IDR" && <div><label className="label">{f.mode === "set" ? "Avg Cost baru per unit (IDR)" : "Cost per unit (IDR)"}</label><input data-testid="adjust-cost" inputMode="decimal" className="nnx-input mono mt-1" value={f.cost_per_unit_idr} onChange={(e) => setF({ ...f, cost_per_unit_idr: e.target.value })} placeholder={f.mode === "set" ? "avg cost baru (default = buy rate)" : "default = buy rate"} /></div>}
        <div><label className="label">Catatan{f.mode === "set" && <span className="text-rose-400"> *</span>}</label><input data-testid="adjust-note" className="nnx-input mt-1" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={f.mode === "set" ? "Wajib: alasan koreksi" : ""} /></div>
      </div>
      <button className="btn-gold w-full mt-4" data-testid="save-adjust" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "SIMPAN STOK"}</button>
    </Modal>
  );
}


function LiquidationModal({ onClose, onDone }) {
  const NB = CURRENCIES.filter((c) => c.code !== "IDR");
  const [f, setF] = useState({ currency: "MYR", amount: "", actual_idr_received: "", provider: "", note: "" });
  const [loading, setLoading] = useState(false);
  const qty = Number(f.amount || 0);
  const idr = Number(f.actual_idr_received || 0);
  const rate = qty > 0 && idr > 0 ? idr / qty : 0;
  const submit = async () => {
    if (!qty || !idr) { toast.error("Jumlah & IDR wajib diisi"); return; }
    setLoading(true);
    try {
      const r = await api.post("/transactions/liquidation", f);
      toast.success(`Liquidation ${f.currency} tercatat. Realized: ${fmtIDR(r.data.gross_profit)}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title="Buang Stock ke Money Changer (Liquidation)" onClose={onClose} testid="liquidation-modal">
      <p className="text-sm text-[#94A3B8] mb-3">Jual stock ke money changer & terima IDR. Realized profit = IDR aktual − (jumlah × valuasi).</p>
      <div className="space-y-3">
        <div><label className="label">Currency</label>
          <select data-testid="liq-currency" className="nnx-input mt-1" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            {NB.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
        </div>
        <div><label className="label">Jumlah dibuang</label><input data-testid="liq-amount" inputMode="decimal" className="nnx-input mono mt-1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="mis. 10000" /></div>
        <div><label className="label">IDR aktual diterima</label><input data-testid="liq-idr" inputMode="decimal" className="nnx-input mono mt-1" value={f.actual_idr_received} onChange={(e) => setF({ ...f, actual_idr_received: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="mis. 38300000" /></div>
        {rate > 0 && <div className="rounded-lg bg-[#0B0E14] border border-[#222B3D] p-3 text-sm flex justify-between"><span className="text-[#94A3B8]">Rate aktual</span><span className="mono text-[#EAB308]" data-testid="liq-actual-rate">{fmtNum(rate)} IDR/{f.currency}</span></div>}
        <div><label className="label">Provider (opsional)</label><input data-testid="liq-provider" className="nnx-input mt-1" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} placeholder="Nama money changer" /></div>
        <div><label className="label">Catatan (opsional)</label><input data-testid="liq-note" className="nnx-input mt-1" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
      </div>
      <button className="btn-gold w-full mt-4" data-testid="save-liquidation" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "CATAT LIQUIDATION"}</button>
    </Modal>
  );
}
